import {
  AutoSave,
  Button,
  Card,
  closeCurrentDialog,
  DataStatus,
  Form,
  Label,
  openDialog,
  Separator,
} from 'platform/components';
import {Box, Heading, HStack, Show, VStack} from 'platform/foundation';

import {useEffect, useRef} from 'react';
import {UseFormReturn} from 'react-hook-form';
import {useDispatch, useStore} from 'react-redux';

import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  CreateMinipurchaseApiArg,
  interestApi,
  MinipurchaseResponseBody,
  UpdateMinipurchaseApiArg,
  useCreateMinipurchaseMutation,
  useUpdateMinipurchaseMutation,
} from '@dms/api/finance';
import {useGetAiLeadEmailMessageQuery} from '@dms/api/sales';
import {castTimeToDateISOString} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  handleApiError,
  Section,
  useInterestEditPermissions,
  EmailLeadPreviewDialog,
  MessageText,
  useTenant,
} from '@dms/shared';

import {EMPTY_PLACEHOLDER, getSafely, handleFormReset} from 'shared/web';

import {useInterestData} from '../../hooks/useInterestData';
import {ExternalFilterConnectorRef} from './components/ExternalFilterConnector';
import {ExternalFilterForm} from './components/ExternalFilterForm';
import {OfferableVehiclesDatagrid} from './components/SellingV2/OfferableVehiclesDatagrid';
import {OfferedVehicles} from './components/SellingV2/OfferedVehicles';
import {PaymentForm} from './components/SellingV2/PaymentForm';
import {TestDriveForm} from './components/SellingV2/TestDriveForm';
import {EXTERNAL_FILTER_ID} from './const/externalFilterId';
import {ExternalFilter} from './types/ExternalFilter';
import {buildMinipurchaseUpdateBody} from './utils/buildMinipurchaseUpdateBody';
import {transformExternalFilterToMinipurchaseSpecification} from './utils/transformExternalFilterToMinipurchaseSpecification';
import {transformMinipurchaseToExternalFilter} from './utils/transformMinipurchaseToExternalFilter';

interface SellingV2Props {
  interestId: string;
}

export function SellingV2(props: SellingV2Props) {
  const dispatch = useDispatch();

  const {interest, miniPurchase, isLoading, isError, offeredVehiclesList} = useInterestData(
    props.interestId
  );

  const {data: aiLeadEmailMessage} = useGetAiLeadEmailMessageQuery(
    {id: interest?.originLeadMessageId ?? ''},
    {
      skip: isNilOrEmpty(interest?.originLeadMessageId),
    }
  );

  const store = useStore();

  // fixedCacheKey stores mutation state in the Redux store under a stable key,
  // allowing us to check if a mutation is in-flight via store.getState() — immune to stale closures
  // caused by AutoSave's debounced callbacks firing from old render cycles.
  const createMinipurchaseCacheKey = `create-minipurchase-${props.interestId}`;
  const [createMinipurchase] = useCreateMinipurchaseMutation({
    fixedCacheKey: createMinipurchaseCacheKey,
  });
  const updateMinipurchaseCacheKey = `update-minipurchase-${props.interestId}`;
  const [updateMinipurchase] = useUpdateMinipurchaseMutation({
    fixedCacheKey: updateMinipurchaseCacheKey,
  });

  const externalFilterRef = useRef<ExternalFilterConnectorRef>(null);
  const pendingMinipurchaseValuesRef = useRef<ExternalFilter | null>(null);

  const {hasChangeMinisalePriceOfferedInterestPermission} = useInterestEditPermissions({
    interestId: props.interestId,
  });

  const {tenantCurrency} = useTenant();

  // Reads create mutation status directly from the Redux store at call time.
  // We cannot use useRef or closure-captured state because AutoSave's useDebouncedCallback
  // fires callbacks from stale closures (old render cycles), making refs unreliable.
  // Also checks 'fulfilled' status to guard the window between POST success and miniPurchase
  // query refetch completing — during which miniPurchase is still null.
  const isMinipurchaseCreateInProgress = () => {
    const state = store.getState() as {
      rtk_omneticApi?: {mutations?: Record<string, {status?: string}>};
    };
    const mutations = state.rtk_omneticApi?.mutations;
    const mutationState = getSafely(mutations, createMinipurchaseCacheKey);
    return mutationState?.status === 'pending' || mutationState?.status === 'fulfilled';
  };

  // Reads update mutation status directly from the Redux store at call time.
  // Prevents concurrent PUT requests that would cause data loss — when a PUT is in-flight,
  // new values are queued in pendingMinipurchaseValuesRef and flushed after the PUT completes
  // using the server response as the base (avoiding stale miniPurchase from closures).
  const isMinipurchaseUpdateInProgress = () => {
    const state = store.getState() as {
      rtk_omneticApi?: {mutations?: Record<string, {status?: string}>};
    };
    const mutations = state.rtk_omneticApi?.mutations;
    const mutationState = getSafely(mutations, updateMinipurchaseCacheKey);
    return mutationState?.status === 'pending';
  };

  // Recursively flushes values queued in pendingMinipurchaseValuesRef after each PUT completes.
  // Called after every successful update to drain any values that accumulated while the PUT was in-flight.
  const flushPendingValues = (base: MinipurchaseResponseBody) => {
    if (!pendingMinipurchaseValuesRef.current) {
      return;
    }

    const pendingValues = pendingMinipurchaseValuesRef.current;
    pendingMinipurchaseValuesRef.current = null;

    const nextArg: UpdateMinipurchaseApiArg = {
      interestId: props.interestId,
      body: buildMinipurchaseUpdateBody(base, pendingValues, tenantCurrency),
    };

    updateMinipurchase(nextArg)
      .unwrap()
      .then((response) => {
        externalFilterRef.current?.onChange(pendingValues);
        flushPendingValues(response);
      })
      .catch(handleApiError);
  };

  const onMiniPurchaseUpdate = (values: ExternalFilter) => {
    if (!miniPurchase || !miniPurchase?.id) {
      if (isMinipurchaseCreateInProgress()) {
        pendingMinipurchaseValuesRef.current = {...pendingMinipurchaseValuesRef.current, ...values};
        return;
      }

      const arg: CreateMinipurchaseApiArg = {
        body: {
          interestId: props.interestId,
          filteredSpecifications: transformExternalFilterToMinipurchaseSpecification(values),
          additionalInformation: values.additionalInformation ?? null,
          testDriveRequested: values.testDriveRequested?.[0] === 'yes',
          testDriveScheduledAt:
            values.testDriveScheduledAtDate && values.testDriveScheduledAtTime
              ? castTimeToDateISOString(
                  values.testDriveScheduledAtDate,
                  values.testDriveScheduledAtTime
                )
              : null,
          testDriveNote: values.testDriveNote,
          preferredDownPayment: values.preferredDownPayment
            ? {
                amount: String(values.preferredDownPayment),
                currency: tenantCurrency,
              }
            : null,
          paymentPreferenceCodeId: values.paymentPreferenceCodeId?.[0] ?? null,
        },
      };
      createMinipurchase(arg)
        .unwrap()
        .then(() => {
          dispatch(interestApi.util.invalidateTags([{type: 'Interest', id: props.interestId}]));
          externalFilterRef.current?.onChange(values);
        })
        .catch(handleApiError);
    } else {
      if (isMinipurchaseUpdateInProgress()) {
        pendingMinipurchaseValuesRef.current = {...pendingMinipurchaseValuesRef.current, ...values};
        return;
      }

      const arg: UpdateMinipurchaseApiArg = {
        interestId: props.interestId,
        body: buildMinipurchaseUpdateBody(miniPurchase, values, tenantCurrency),
      };
      updateMinipurchase(arg)
        .unwrap()
        .then((response) => {
          externalFilterRef.current?.onChange(values);
          flushPendingValues(response);
        })
        .catch(handleApiError);
    }
  };

  // Flush pending values after miniPurchase becomes available (POST completed + interest refetched)
  useEffect(() => {
    if (!miniPurchase?.id) {
      return;
    }

    if (pendingMinipurchaseValuesRef.current) {
      const pendingValues = pendingMinipurchaseValuesRef.current;
      pendingMinipurchaseValuesRef.current = null;
      onMiniPurchaseUpdate(pendingValues);
    }
    // onMiniPurchaseUpdate intentionally omitted: including it would cause an infinite loop since
    // it depends on miniPurchase, which changes when this effect flushes pending values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miniPurchase?.id]);

  const handleOpenEmailLeadPreviewDialog = () => {
    openDialog(<EmailLeadPreviewDialog message={aiLeadEmailMessage!} />, {
      id: 'emailLeadPreviewDialog',
      size: 'large',
      title: i18n.t('entity.interest.aiLead.sentEmail'),
      buttons: [
        {
          title: i18n.t('general.actions.close'),
          variant: 'secondary',
          onClick: closeCurrentDialog,
        },
      ],
    });
  };

  const resetRequestSpecification = (formApi: UseFormReturn<ExternalFilter>) => {
    handleFormReset<ExternalFilter>(formApi, [
      {name: 'type', value: ['VEHICLETYPE_PASSENGER_CAR']},
      {name: 'make', value: null},
      {name: 'modelFamily', value: null},
      {name: 'trimLevel', value: null},
      {name: 'fuelType', value: null},
      {name: 'transmission', value: null},
      {name: 'bodyStyle', value: null},
      {name: 'drive', value: null},
      {name: 'features', value: null},
      {name: 'vatDeductible', value: null},
      {name: 'bodyColor', value: null},
      {name: 'labelIds', value: null},
      {name: 'salePresetId', value: null},
      {name: 'cubicCapacityFrom', value: null},
      {name: 'cubicCapacityTo', value: null},
      {name: 'priceFrom', value: null},
      {name: 'priceTo', value: null},
      {name: 'yearFrom', value: null},
      {name: 'yearTo', value: null},
      {name: 'mileageFrom', value: null},
      {name: 'mileageTo', value: null},
      {name: 'powerFrom', value: null},
      {name: 'powerTo', value: null},
      {name: 'condition', value: null},
      {name: 'additionalInformation', value: null},
    ]);
    onMiniPurchaseUpdate(formApi.getValues());
  };

  const isInterestClosedAnyStatus =
    interest?.state === 'CLOSED' || interest?.state === 'UNSUCCESSFUL';

  return (
    <Section>
      <DataStatus isLoading={isLoading} isError={isError} minHeight={175}>
        <Show when={isNotNilOrEmpty(interest)}>
          <VStack spacing={4}>
            <Card title={i18n.t('entity.interest.aiLead.customerInquiry')}>
              <VStack spacing={6}>
                <Show when={isNotNilOrEmpty(interest?.originLeadMessageId)}>
                  <VStack spacing={1}>
                    <HStack width="100%" justify="space-between" align="flex-end">
                      <Label>{i18n.t('entity.interest.aiLead.originalInquiry')}</Label>
                      <Button
                        size="small"
                        variant="link"
                        title={i18n.t('entity.interest.aiLead.showOriginal')}
                        onClick={() => handleOpenEmailLeadPreviewDialog()}
                        isDisabled={isNilOrEmpty(aiLeadEmailMessage)}
                      />
                    </HStack>
                    <Box
                      padding={4}
                      border="1px solid"
                      borderColor="palettes.neutral.40.100"
                      backgroundColor="palettes.neutral.10.100"
                      borderRadius="medium"
                    >
                      <MessageText htmlData={aiLeadEmailMessage?.body ?? EMPTY_PLACEHOLDER} />
                    </Box>
                  </VStack>
                </Show>

                <Show when={isNotNilOrEmpty(offeredVehiclesList)}>
                  <OfferedVehicles interestId={props.interestId} hideScheduleButton />
                </Show>

                <TestDriveForm
                  interestId={props.interestId}
                  onMinipurchaseUpdate={onMiniPurchaseUpdate}
                  hideScheduleButton
                  isDisabled={isInterestClosedAnyStatus}
                />

                <Separator spacing={0} />

                <PaymentForm
                  interestId={props.interestId}
                  onMinipurchaseUpdate={onMiniPurchaseUpdate}
                  isDisabled={isInterestClosedAnyStatus}
                />

                <Separator spacing={0} />

                <Form<ExternalFilter>
                  data-testid={testIds.interest.detail('requestSpecification')}
                  defaultValues={transformMinipurchaseToExternalFilter(miniPurchase)}
                  isDisabled={
                    !hasChangeMinisalePriceOfferedInterestPermission || isInterestClosedAnyStatus
                  }
                >
                  {(control, formApi) => (
                    <VStack spacing={6}>
                      <HStack width="100%" justify="space-between" align="flex-end">
                        <Heading size={4}>
                          {i18n.t('entity.interest.labels.requestSpecification')}
                        </Heading>
                        <Button
                          size="small"
                          variant="link"
                          isDisabled={
                            isInterestClosedAnyStatus ||
                            !hasChangeMinisalePriceOfferedInterestPermission
                          }
                          title={i18n.t('entity.interest.actions.resetSpecification')}
                          leftIcon="action/delete"
                          onClick={() => resetRequestSpecification(formApi)}
                          data-testid={testIds.interest.detail('resetRequestSpecification')}
                        />
                      </HStack>
                      <Box minHeight={125}>
                        <AutoSave<ExternalFilter> onSave={onMiniPurchaseUpdate} />
                        <ExternalFilterForm
                          data-testid={testIds.interest.detail('requestSpecification')}
                          control={control}
                          formApi={formApi}
                          onChange={onMiniPurchaseUpdate}
                        />
                        <div id={EXTERNAL_FILTER_ID} />
                      </Box>
                    </VStack>
                  )}
                </Form>
              </VStack>
            </Card>
            <Box height={175}>
              <OfferableVehiclesDatagrid
                interestId={props.interestId}
                externalFilterRef={externalFilterRef}
              />
            </Box>
          </VStack>
        </Show>
      </DataStatus>
    </Section>
  );
}
